import { rectContains } from '../core/math';
import type { Point } from '../core/types';
import { patchSelection } from '../engine/ops';
import { rasterizePolygon } from '../selection/shapes';
import { applySelectionShape, commitHistory } from '../state/actions';
import { paintableLayer, type Tool, type ToolContext } from './types';

const MIN_DISTANCE = 1.5;

/**
 * Patch works in two moves, exactly as Photopea describes it: outline the flaw,
 * then drag the outline onto clean pixels to borrow them. Pressing inside an
 * existing selection starts the drag; pressing anywhere else draws a new one.
 */
export const patchTool: Tool = (() => {
  let outline: Point[] = [];
  let dragStart: Point | null = null;
  let dragTo: Point | null = null;

  const selectionBounds = (ctx: ToolContext) =>
    ctx.engine.resourcesFor(ctx.doc).selection.bounds();

  return {
    id: 'patch',
    cursor: 'crosshair',
    hint: 'Outline the flaw, then drag the outline onto clean pixels',

    onPointerDown(event, ctx) {
      const bounds = selectionBounds(ctx);
      if (bounds && rectContains(bounds, event.point)) {
        dragStart = event.point;
        dragTo = event.point;
        ctx.requestRender();
        return;
      }
      outline = [event.point];
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      if (dragStart) {
        dragTo = event.point;
        ctx.requestRender();
        return;
      }
      if (outline.length === 0) return;
      const last = outline[outline.length - 1];
      if (Math.hypot(event.point.x - last.x, event.point.y - last.y) < MIN_DISTANCE) return;
      outline.push(event.point);
      ctx.requestRender();
    },

    onPointerUp(event, ctx) {
      if (dragStart) {
        const offset = { x: event.point.x - dragStart.x, y: event.point.y - dragStart.y };
        dragStart = null;
        dragTo = null;
        const layerId = paintableLayer(ctx.doc);
        if (!layerId) {
          ctx.setStatus('The active layer is locked, or has no pixels to patch');
          return;
        }
        if (Math.hypot(offset.x, offset.y) < 1) {
          ctx.setStatus('Drag the outline onto the pixels to borrow');
          return;
        }
        const ops = patchSelection(ctx.engine, ctx.doc, layerId, offset);
        commitHistory(ctx.doc.id, 'Patch', ops);
        ctx.requestRender();
        return;
      }

      const path = outline;
      outline = [];
      if (path.length >= 3) {
        const raster = rasterizePolygon(path, ctx.doc.width, ctx.doc.height);
        if (raster) applySelectionShape(raster, 'replace');
      }
      ctx.requestRender();
    },

    cancel(ctx) {
      outline = [];
      dragStart = null;
      dragTo = null;
      ctx.requestRender();
    },

    drawOverlay(overlay, ctx) {
      const { ctx: c } = overlay;

      if (dragStart && dragTo) {
        const bounds = selectionBounds(ctx);
        if (!bounds) return;
        const dx = dragTo.x - dragStart.x;
        const dy = dragTo.y - dragStart.y;
        const a = overlay.toScreen({ x: bounds.x + dx, y: bounds.y + dy });
        const b = overlay.toScreen({
          x: bounds.x + bounds.width + dx,
          y: bounds.y + bounds.height + dy,
        });
        c.save();
        c.setLineDash([4, 4]);
        c.strokeStyle = 'rgba(120,190,255,0.95)';
        c.lineWidth = 1;
        c.strokeRect(a.x + 0.5, a.y + 0.5, b.x - a.x, b.y - a.y);
        c.restore();
        return;
      }

      if (outline.length < 2) return;
      c.save();
      c.setLineDash([4, 4]);
      c.strokeStyle = 'rgba(255,255,255,0.9)';
      c.lineWidth = 1;
      c.beginPath();
      const first = overlay.toScreen(outline[0]);
      c.moveTo(first.x, first.y);
      for (let i = 1; i < outline.length; i += 1) {
        const p = overlay.toScreen(outline[i]);
        c.lineTo(p.x, p.y);
      }
      c.closePath();
      c.stroke();
      c.restore();
    },
  };
})();
